// src/components/inbox/inboxFormat.js
import moment from "moment";
import { dayLabel } from "../thread/threadFormat";
import { PILL_PLAN_ENDED } from "../plan/planCopy";

// Pooled inbox SP2 — copy and formatting for the advisor's inbox. Copy is
// verbatim from the SP2 spec § Copy: "·" is U+00B7, "…" is U+2026, and
// apostrophes are straight. Times render in the browser's zone with moment,
// as SP1's thread does; the day word is SP1's dayLabel.
export const INBOX_TITLE = "Inbox";
export const INBOX_SUBTITLE = "All clients";
export const EMPTY_LIST = "Nothing waiting on you.";
export const NO_MESSAGES_GROUP = "No messages yet";
export const LIST_FOOTER =
  "Sorted by how long the client has waited. Every open is recorded.";
export const CHIP_MINE = "Mine";
export const CHIP_ALL = "All";
export const PILL_NEW = "new";
export const PILL_OVERDUE = "overdue";
// A failed list, thread or tab load with no server message (R71), in SP1's
// "Couldn't send. Try again." voice.
export const LOAD_FAILED = "Couldn't load. Try again.";
export const SAVE_FAILED = "Couldn't save. Try again.";
export const TRY_AGAIN = "Try again";

export const waitingChip = (n) =>
  n == null ? "Waiting on us" : `Waiting on us · ${n}`;
export const ownedThreadLabel = (first) => `${first}'s thread`;
export const bookedWithPill = (first) => `booked with ${first}`;

// "Jordan M." — first name and last initial, as the comp draws a row.
export function shortName(person) {
  if (!person) return "";
  const initial = person.lastName ? ` ${person.lastName[0]}.` : "";
  return `${person.firstName || ""}${initial}`;
}

export const fullName = (person) =>
  person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : "";

// "[First] (you)" when the person is the signed-in advisor.
export const withYou = (person, viewerId) =>
  `${person?.firstName || ""}${
    viewerId && person?._id && String(person._id) === String(viewerId)
      ? " (you)"
      : ""
  }`;

// The day word: SP1's dayLabel inside its week window ("Today", "Yesterday",
// "Tuesday"); beyond it, or ahead of now, "MMM D" with no year (spec § Dates,
// R67). dayLabel's own fallback is "MMM D, YYYY", recognised by its comma.
export function inboxDay(iso, now = new Date()) {
  const day = dayLabel(iso, now);
  return day.includes(",") ? moment(iso).format("MMM D") : day;
}

// "6:52 PM" today; "Yesterday 6:52 PM", "Tuesday 6:52 PM" or "Sep 3 6:52 PM"
// otherwise: the spec's "[Day] [h:mm A]", capitalised as the comp draws it.
export function atPhrase(iso, now = new Date()) {
  const time = moment(iso).format("h:mm A");
  const day = inboxDay(iso, now);
  return day === "Today" ? time : `${day} ${time}`;
}

// "today 9:05 AM", "yesterday 4:12 PM", "tuesday 4:12 PM": the spec's
// lower-case "[day] [h:mm A]", lower-cased as the comp does (R67). A date
// ("Sep 3") is not a day word and keeps its case.
export function answeredAt(iso, now = new Date()) {
  const day = inboxDay(iso, now);
  const word = day.includes(" ") ? day : day.toLowerCase();
  return `${word} ${moment(iso).format("h:mm A")}`;
}

export function factLine(row, now = new Date()) {
  if (row.waitingOnUs && row.waitingSince)
    return `wrote ${atPhrase(row.waitingSince, now)}`;
  if (row.lastAnsweredBy?.firstName)
    return `answered by ${row.lastAnsweredBy.firstName}`;
  return null;
}

export const paidLine = (row) =>
  row.paidSince
    ? `Paid ${row.paidSince}, has not written. Open the thread.`
    : "Has not written. Open the thread.";

export function rowPills(row) {
  const pills = [];
  if (row.owner?.firstName && !row.ownerIsMe)
    pills.push(ownedThreadLabel(row.owner.firstName));
  if (row.bookedWith?.firstName)
    pills.push(bookedWithPill(row.bookedWith.firstName));
  if (row.isNew) pills.push(PILL_NEW);
  if (row.overdue) pills.push(PILL_OVERDUE);
  // SP5 (owner item 13): a lapsed client stays listed, readable, with the tag.
  if (row.planEnded) pills.push(PILL_PLAN_ENDED);
  return pills;
}

// ---- W4: the open thread --------------------------------------------------
export const EMPTY_THREAD_LINE =
  "No messages yet. Your message opens the thread.";
export const YOUR_THREAD = "Your thread";
export const TAKE_OVER = "Take over";
export const TAKE_OVER_DONE = "This thread is now yours.";
export const COMPOSER_HELPER = "Sent under your name.";
export const composerPlaceholder = (first) => `Write to ${first || ""}…`;
export const sendAsLabel = (first) => (first ? `Send as ${first}` : "Send");
export const takeOverPrompt = (first) =>
  `Take over ${first}'s thread? They will see it as yours.`;

// The contract's waitingOnUs rule, applied to the open thread's own fields.
export const isWaiting = (thread) =>
  !!thread?.lastClientMessageAt &&
  (!thread.lastAdvisorMessageAt ||
    new Date(thread.lastClientMessageAt) >
      new Date(thread.lastAdvisorMessageAt));

// The newest saved advisor reply in a page ordered oldest-first.
export function lastAdvisorReply(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.senderType === "advisor" && m.kind !== "welcome" && !m.pending)
      return m;
  }
  return null;
}

export function threadHeaderLine({
  thread,
  messages = [],
  row = null,
  viewerId = null,
  now = new Date(),
}) {
  if (!thread) return null;
  if (isWaiting(thread))
    return `Waiting since ${atPhrase(thread.lastClientMessageAt, now)}. Nobody has replied.`;
  if (thread.lastAdvisorMessageAt) {
    const by =
      lastAdvisorReply(messages)?.sender ?? row?.lastAnsweredBy ?? null;
    if (!by?.firstName) return null;
    return `Last answered by ${withYou(by, viewerId)}, ${answeredAt(thread.lastAdvisorMessageAt, now)}`;
  }
  return EMPTY_THREAD_LINE;
}

// ---- W5: the client record --------------------------------------------------
// Dollars as the comp and ClientDashboard print them: cents only when present.
export function formatMoney(n) {
  const value = Number(n) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export const signedAmount = ({ amount, direction }) =>
  `${direction === "credit" ? "+" : "-"}${formatMoney(Math.abs(Number(amount) || 0))}`;

export function formatSize(bytes) {
  if (bytes == null) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Appointment dates are UTC-midnight calendar dates (UserMeetings.jsx reads
// them with moment.utc); Plaid dates are 'YYYY-MM-DD' calendar-day strings.
export function calendarDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value))
    return moment(value, "YYYY-MM-DD").format("MMM D");
  return moment.utc(value).format("MMM D");
}

export const instantDate = (value) =>
  value ? moment(value).format("MMM D") : "";

// The stored "HH:mm", shown the way UserMeetings.jsx:167 shows it.
export function apptTime(time) {
  if (!time) return "";
  const parsed = moment.utc(time, "HH:mm", true);
  return parsed.isValid() ? parsed.format("h:mm A") : time;
}

// Only http(s) is navigable (SEC-037, UserDocuments.jsx safeDocumentUrl): an
// href is server-supplied data, and a javascript: URI runs on click.
export function safeHttpUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:"
      ? parsed.href
      : null;
  } catch (_) {
    return null;
  }
}

export const uploadMeta = (upload) =>
  [
    formatSize(upload.size),
    instantDate(upload.uploadedAt),
    upload.from === "chat" ? "from the thread" : "from the Documents page",
  ]
    .filter(Boolean)
    .join(" · ");

export const planSub = (plan) =>
  [plan?.price, plan?.since && `since ${plan.since}`]
    .filter(Boolean)
    .join(" · ");

export const meetingsValue = (m) =>
  m?.available == null ? `${m?.used ?? 0} used` : `${m.available} available`;

export const meetingsSub = (m) =>
  m?.included == null
    ? ""
    : `${m.used} of ${m.included} used${m.purchased ? `, ${m.purchased} bought` : ""}`;

export const accountsLabel = (n) => `${n} ${n === 1 ? "account" : "accounts"}`;

// ---- Pooled inbox SP3 (W2): the client note and the Privacy Notice line --------
// Copy verbatim from the SP3 spec § Advisor inbox — client-level note.
export const clientNoteLine = (first) =>
  `Note about ${first}. One note per client. Saving replaces it; previous versions are kept.`;
export const noClientNoteYet = (first) => `No note about ${first} yet.`;
export const noteSavedLine = (iso, first) =>
  `Saved ${instantDate(iso)} by ${first}`;
export const privacyNoticeLine = (notice) =>
  notice
    ? [
        "Privacy Notice",
        `Delivered ${instantDate(notice.deliveredAt)}`,
        notice.editionLabel,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Not yet acknowledged";
